import QueryStringHandler from './shared/QueryStringHandler';
import { throttleSearch } from './shared/common';
import { isExperienceEditor } from './shared/helpers';

interface CategoryItem {
    ScName: string;
    Name: string;
}

type CategoryData = Record<string, CategoryItem[]>;

class EikaAdvisorList {
    public privateCorpFilterElm!: HTMLSelectElement;

    public locationFilterElm!: HTMLSelectElement;

    public departmentFilterElm!: HTMLSelectElement;

    public searchFilterElm!: HTMLInputElement;

    public dataCategoriesElm!: HTMLInputElement;

    public screenReaderStatusElm!: HTMLElement;

    public filterElDepartment!: HTMLElement;

    public errorContainer!: HTMLElement;

    public advisorsContainer!: HTMLElement;

    public container!: HTMLElement;

    public categoryData!: CategoryData;

    private isExperienceEditor: boolean = false;

    private urlHandler!: QueryStringHandler;

    constructor(container: HTMLElement) {
        this.isExperienceEditor = isExperienceEditor(window.location.href);

        if (container) {
            this.container = container;
            this.privateCorpFilterElm = container.querySelector('#advisorPrivateCorp') as HTMLSelectElement;
            this.locationFilterElm = container.querySelector('#advisorFilterLocation') as HTMLSelectElement;
            this.departmentFilterElm = container.querySelector('#advisorFilterDepartment') as HTMLSelectElement;
            this.searchFilterElm = container.querySelector('#advisorFilterSearch') as HTMLInputElement;
            this.dataCategoriesElm = container.querySelector('#categoryData') as HTMLInputElement;
            this.screenReaderStatusElm = container.querySelector('#js-sr-results') as HTMLElement;
            this.filterElDepartment = container.querySelector('#advisor-filter-department') as HTMLElement;
            this.errorContainer = container.querySelector('#advisor-card-missing') as HTMLElement;
            this.advisorsContainer = container.querySelector('.sgw-advisor-card__container') as HTMLElement;

            this.urlHandler = new QueryStringHandler();
            this.setEventListeners();
        }
    }

    public initialize(): void {
        if (!this.hasAllFilterElements()) return;

        this.privateCorpFilterElm.value = this.urlHandler?.get('area') ?? '*';
        this.locationFilterElm.value = this.urlHandler?.get('location') ?? '*';
        this.departmentFilterElm.value = this.urlHandler?.get('department') ?? '*';
        this.searchFilterElm.value = this.urlHandler?.get('query') ?? '';

        const rawData = this.dataCategoriesElm.value;
        const data = JSON.parse(rawData);

        if (data) {
            this.categoryData = data;
        }
        this.filterAdvisors();
    }

    public updateScreenReaderStatus(): void {
        if (this.screenReaderStatusElm) {
            const count = document.querySelectorAll<HTMLElement>(
                '.sgw-advisor-card:not([style*="display: none"])',
            ).length;
            this.screenReaderStatusElm.textContent = count.toString();
        }
    }

    /**
     * Filter the Advisor-cards
     */
    private filterAdvisors() {
        if (!this.hasAllFilterElements()) return;

        const { area, location, department, search } = this.getFilteredValues();
        const advisors = document.querySelectorAll<HTMLElement>('.sgw-advisor-card');
        const uniqueCategories = new Set<string>();
        let anyVisible = false;

        advisors.forEach((card) => {
            const getAdvisorName = card.querySelector('.sgw-advisor-card__content--name');
            const advisorPrivateCorp = card.getAttribute('data-privatecorp') ?? '*';
            const advisorLocation = card.getAttribute('data-location') ?? '*';
            const advisorDepartment = card.getAttribute('data-category') ?? '*';
            const advisorName =
                getAdvisorName && getAdvisorName.textContent !== null ? getAdvisorName.textContent.toLowerCase() : '';

            const privateCorpMatches = area === '*' || advisorPrivateCorp.split(',').includes(area);
            const locationMatches = location === '*' || advisorLocation.split(',').includes(location);
            const departmentMatches = department === '*' || advisorDepartment.split(',').includes(department);
            const nameMatches = advisorName.includes(search);

            const matchesWithoutDepartment = privateCorpMatches && locationMatches && nameMatches;
            const isMatch = matchesWithoutDepartment && departmentMatches;

            card.style.display = isMatch ? '' : 'none';

            if (matchesWithoutDepartment) {
                const category = card.getAttribute('data-category');
                if (category && category !== '*') {
                    category
                        .split(',')
                        .map((c) => {
                            return c.trim();
                        })
                        .filter(Boolean)
                        .forEach((c) => {
                            uniqueCategories.add(c);
                        });
                }
            }

            if (isMatch) {
                anyVisible = true;
            }
        });

        this.updateScreenReaderStatus();

        Array.from(this.departmentFilterElm.options).forEach((option) => {
            if (option.value === '*') {
                option.hidden = false;
                return;
            }
            option.hidden = !uniqueCategories.has(option.value);
        });

        if (!anyVisible) {
            this.container.classList.add('sgw-advisorlist--no-results');
        } else {
            this.container.classList.remove('sgw-advisorlist--no-results');
        }

        if (this.errorContainer) {
            if (anyVisible) {
                this.errorContainer.removeAttribute('role');
            } else {
                this.errorContainer.setAttribute('role', 'alert');
            }
            this.errorContainer.style.display = anyVisible ? 'none' : '';
        }
        if (this.advisorsContainer) {
            this.advisorsContainer.style.display = !anyVisible ? 'none' : '';
        }

        if (!this.isExperienceEditor) {
            if (area !== '*') {
                this.urlHandler.set('area', area);
            } else {
                this.urlHandler.delete('area');
            }
            if (location !== '*') {
                this.urlHandler.set('location', location);
            } else {
                this.urlHandler.delete('location');
            }
            if (department !== '*') {
                this.urlHandler.set('department', department);
            } else {
                this.urlHandler.delete('department');
            }
            if (search !== '') {
                this.urlHandler.set('query', search);
            } else {
                this.urlHandler.delete('query');
            }
            this.urlHandler.pushState();
        }
    }

    /**
     * Update the filters based on the json object with available categories
     */
    private updateCategories(): void {
        const selected = this.privateCorpFilterElm.value;
        const { categoryData } = this;
        let items: CategoryItem[] = [];

        if (!selected || !categoryData) return;

        while (this.departmentFilterElm.options.length > 1) {
            this.departmentFilterElm.remove(1);
        }

        if (selected === '*') {
            items = Object.values(categoryData).flat();
        } else if (categoryData[selected]) {
            items = categoryData[selected];
        }

        items.forEach((item) => {
            const option = document.createElement('option');
            option.value = item.ScName;
            option.textContent = item.Name;
            this.departmentFilterElm.appendChild(option);
        });

        this.filterAdvisors();
    }

    /**
     * Get object of selected values from the dropdowns and search
     */
    private getFilteredValues() {
        const area = this.privateCorpFilterElm.value;
        const location = this.locationFilterElm.value;
        const department = this.departmentFilterElm.value;
        const search = this.searchFilterElm.value.toLowerCase();

        return { area, location, department, search };
    }

    /**
     * Just a check to see if we have all filter-elements
     */
    private hasAllFilterElements(): boolean {
        return !!(
            this.privateCorpFilterElm &&
            this.locationFilterElm &&
            this.departmentFilterElm &&
            this.searchFilterElm
        );
    }

    /**
     * Set the event-listeners for the filters (and trottle the search-field)
     */
    private setEventListeners(): void {
        this.privateCorpFilterElm?.addEventListener('change', this.filterAdvisors.bind(this));
        this.locationFilterElm?.addEventListener('change', this.filterAdvisors.bind(this));
        this.departmentFilterElm?.addEventListener('change', this.filterAdvisors.bind(this));
        this.searchFilterElm?.addEventListener(
            'input',
            throttleSearch(() => {
                this.filterAdvisors();
            }, 600),
        );
    }
}

export default function AdvisorListWithSorting(id: string) {
    document.addEventListener('DOMContentLoaded', () => {
        const advisorlist = document.getElementById(id);
        if (advisorlist) {
            const Advisors = new EikaAdvisorList(advisorlist);
            Advisors.initialize();
        }
    });
}
